import React, { useEffect } from "react";
import { Container, Spinner, Card, CardBody, Button } from "reactstrap";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { completeOAuthRequest } from "store/integrations/action";
import { ApplicationState } from "store";
import { IntegrationActionTypes } from "store/integrations/types";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";

const OAuthCallback: React.FC = () => {
  const { appId }  = useParams<{ appId?: string }>();
  const { search } = useLocation();
  const navigate   = useNavigate();
  const dispatch   = useDispatch<ThunkDispatch<any, any, Action>>();
  const tenantId   = useSelector((s: ApplicationState) => s.auth.userProfile.tenant.id);

  const code = new URLSearchParams(search).get("code");
  const {
    loadingOAuthComplete,
    errorOAuthComplete,
    oauthCompleteData,
  } = useSelector((s: ApplicationState) => s.integration);

  // Kick off the OAuth-complete flow once
  useEffect(() => {
    if (!code || !appId) {
      dispatch({
        type: IntegrationActionTypes.COMPLETE_OAUTH_ERROR,
        error: new Error("Invalid OAuth parameters"),
      });
      return;
    }
    if (tenantId) {
      dispatch(completeOAuthRequest(tenantId, appId, code));
    }
  }, [code, appId, tenantId, dispatch]);

  // When we get data → navigate
  useEffect(() => {
    if (oauthCompleteData) {
      navigate(
        `/integrations/setup/${oauthCompleteData.id}`
      );
    }
  }, [oauthCompleteData, navigate]);

  // Render based solely on Redux state
  let content;
  if (loadingOAuthComplete) {
    content = (
      <div className="text-center">
        <Spinner size="lg" className="mb-3" />
        <h5 className="fw-semibold text-secondary">Finalizing connection…</h5>
      </div>
    );
  } else if (errorOAuthComplete) {
    content = (
      <div className="text-center">
        <i className="ri-close-circle-line text-danger display-3 mb-3" />
        <h4 className="fw-bold text-danger mb-2">Connection Failed</h4>
        <p className="text-muted mb-4">{errorOAuthComplete}</p>
        <Button color="danger" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  } else {
    content = (
      <div className="text-center">
        <Spinner size="lg" className="mb-3" />
        <h5 className="fw-semibold text-secondary">Initializing…</h5>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container
          fluid
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "calc(100vh - 70px)" }}
        >
          <Card className="shadow-lg rounded-3 border-0 p-4" style={{ maxWidth: 400, width: "100%" }}>
            <CardBody>{content}</CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default OAuthCallback;
